import { useSyncExternalStore } from "react";

const STORAGE_KEY = "gada_shop_cart";
const listeners = new Set();
const EMPTY_CART = [];
let cartSnapshot = EMPTY_CART;

function notify() {
  listeners.forEach((listener) => listener());
}

function parseCart() {
  if (typeof window === "undefined") {
    return EMPTY_CART;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : EMPTY_CART;
  } catch (_error) {
    return EMPTY_CART;
  }
}

function writeCart(items) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  cartSnapshot = items;
  notify();
}

function getSnapshot() {
  const items = parseCart();

  if (items === EMPTY_CART && cartSnapshot.length === 0) {
    return cartSnapshot;
  }

  const nextSerialized = JSON.stringify(items);
  const currentSerialized = JSON.stringify(cartSnapshot);

  if (nextSerialized !== currentSerialized) {
    cartSnapshot = items;
  }

  return cartSnapshot;
}

export function getCartItems() {
  return getSnapshot();
}

export function addCartItem(item) {
  const items = [...getSnapshot()];
  const existing = items.find((entry) => entry.productId === item.productId);

  if (existing) {
    existing.quantity += Number(item.quantity || 1);
    existing.notes = item.notes !== undefined ? item.notes : existing.notes;
    existing.price = item.price;
    existing.currency = item.currency;
    existing.imageUrl = item.imageUrl;
    existing.categoryName = item.categoryName;
    existing.productType = item.productType;
  } else {
    items.push({
      productId: item.productId,
      name: item.name,
      quantity: Number(item.quantity || 1),
      price: Number(item.price || 0),
      currency: item.currency || "EUR",
      imageUrl: item.imageUrl || null,
      categoryName: item.categoryName || null,
      productType: item.productType || "standard",
      notes: item.notes || "",
    });
  }

  writeCart(items);
}

export function updateCartItem(productId, patch) {
  const items = getSnapshot().map((item) =>
    item.productId === productId
      ? {
          ...item,
          ...patch,
          quantity: Math.max(1, Number(patch.quantity ?? item.quantity ?? 1)),
        }
      : item
  );

  writeCart(items);
}

export function removeCartItem(productId) {
  writeCart(getSnapshot().filter((item) => item.productId !== productId));
}

export function clearCart() {
  writeCart([]);
}

export function useShopCart() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot,
    () => EMPTY_CART
  );
}
